import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as mentorApi from '../../api/mentorApi';
import { YEAR2_APPROVED_TOPICS } from '../../data/year2Topics';
import { resolveStudents } from '../../utils/studentResolution';
import {
  chunkIntoColumns,
  formatStudentDisplayName,
  isSmokeTestSquadName,
  mapCohortStudentsFromSetup,
  resolveSchoolDisplayName,
} from '../../utils/year2Ui';
import {
  assignedStudentMap,
  autoDistributeStudents,
  findDuplicateTopics,
  formatSetupStatus,
  mergeSetupSquads,
  requiredSquadCount,
} from '../../utils/year2SquadSetup';
import './year2SquadCreator.css';

function StudentChip({ student, fromSlot, isAssigned, disabled, onDragStart, onClick, showStatus = true }) {
  const displayName = formatStudentDisplayName(student.name);
  const gradeLabel = student.grade || 'Class not listed';
  return (
    <div
      draggable={!disabled}
      onDragStart={() => !disabled && onDragStart(student.id, fromSlot)}
      onClick={() => !disabled && onClick?.(student.id)}
      className={`year2-student-chip ${isAssigned ? 'assigned' : ''} ${disabled ? 'disabled' : ''}`}
    >
      <div className="year2-student-chip-main">
        <span className="year2-student-name">{displayName}</span>
        <span className="year2-student-grade">{gradeLabel}</span>
      </div>
      {showStatus && (
        <span className={`year2-student-status ${isAssigned ? 'assigned' : 'unassigned'}`}>
          {isAssigned ? 'Assigned' : 'Unassigned'}
        </span>
      )}
    </div>
  );
}

function SquadMemberRow({ student, slotIndex, isLocked, onDragStart, onRemove }) {
  const displayName = formatStudentDisplayName(student.name);
  const gradeLabel = student.grade || 'Class not listed';

  return (
    <div
      className="year2-squad-member-row"
      draggable={!isLocked}
      onDragStart={() => !isLocked && onDragStart(student.id, slotIndex)}
    >
      <div className="year2-squad-member-text">
        <span className="year2-squad-member-name">{displayName}</span>
        <span className="year2-squad-member-grade">{gradeLabel}</span>
      </div>
      {!isLocked && (
        <button
          type="button"
          className="btn-form-toggle-outlined year2-squad-member-remove"
          aria-label={`Remove ${displayName}`}
          onClick={() => onRemove(slotIndex, student.id)}
        >
          ×
        </button>
      )}
    </div>
  );
}

function SquadBox({
  squad,
  slotIndex,
  students,
  isLocked,
  isDragOver,
  onDragOverEnter,
  onDragOverLeave,
  onDrop,
  onDragStart,
  onNameChange,
  onTopicChange,
  onRemoveMember,
}) {
  const memberRows = (squad.member_ids || [])
    .map((id) => students.find((s) => String(s.id) === String(id)))
    .filter(Boolean);
  const displayName = isSmokeTestSquadName(squad.name)
    ? `Squad ${String.fromCharCode(65 + slotIndex)}`
    : squad.name;
  const nameDisabled = isLocked || squad.name_locked;

  return (
    <div
      className={`year2-squad-card ${isDragOver ? 'drag-over' : ''}`}
      onDragOver={(e) => { e.preventDefault(); onDragOverEnter(); }}
      onDragLeave={onDragOverLeave}
      onDrop={(e) => { e.preventDefault(); onDrop(slotIndex); }}
    >
      <div className="year2-squad-card-header">
        <input
          type="text"
          className="form-control-input"
          value={displayName}
          disabled={nameDisabled}
          onChange={(e) => onNameChange(slotIndex, e.target.value)}
          placeholder="Squad name"
          title={squad.name_locked ? 'Squad name is now managed from LMS and can no longer be edited here.' : undefined}
          style={{ flex: 1 }}
        />
        <span style={{ fontSize: '12px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
          {memberRows.length} members
        </span>
      </div>

      <select
        className="form-control-select"
        value={squad.topic}
        disabled={isLocked}
        onChange={(e) => onTopicChange(slotIndex, e.target.value)}
        style={{ marginBottom: '12px', width: '100%' }}
      >
        <option value="">Select topic…</option>
        {YEAR2_APPROVED_TOPICS.map((topic) => (
          <option key={topic} value={topic}>{topic}</option>
        ))}
      </select>

      <div className="year2-squad-members">
        {memberRows.length > 0 ? memberRows.map((student) => (
          <SquadMemberRow
            key={student.id}
            student={student}
            slotIndex={slotIndex}
            isLocked={isLocked}
            onDragStart={onDragStart}
            onRemove={onRemoveMember}
          />
        )) : (
          <div className="transfer-empty-state">Drop students here</div>
        )}
      </div>
    </div>
  );
}

export default function Year2SquadCreator({
  cohortId,
  students,
  liveSchools = [],
  onBack,
  onSaved,
  onError,
  onSuccess,
}) {
  const [setup, setSetup] = useState(null);
  const [squads, setSquads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [dragOverSlot, setDragOverSlot] = useState(null);
  const [rosterSearch, setRosterSearch] = useState('');
  const dragStudentId = useRef(null);
  const dragFromSlot = useRef(null);
  // BUG-year2-squad-setup-error-reload-01: parent passes unstable showToast as onError/onSuccess.
  // If those callbacks are in loadSetup deps, every toast re-creates loadSetup and the mount
  // effect re-GETs setup — wiping in-progress (and failed-save) local assignments.
  const onErrorRef = useRef(onError);
  const onSuccessRef = useRef(onSuccess);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  useEffect(() => { onSuccessRef.current = onSuccess; }, [onSuccess]);

  const loadSetup = useCallback(async () => {
    setLoading(true);
    try {
      const data = await mentorApi.getYear2SquadSetup(cohortId);
      setSetup(data);
      const required = requiredSquadCount(data.student_count);
      setSquads(mergeSetupSquads(data.squads, required, data.student_ids));
      setIsLocked(data.setup_status === 'finalized');
    } catch (err) {
      onErrorRef.current?.(err?.message || 'Failed to load squad setup');
      setSetup(null);
    } finally {
      setLoading(false);
    }
  }, [cohortId]);

  useEffect(() => {
    loadSetup();
  }, [loadSetup]);

  const schoolDisplayName = useMemo(() => {
    if (!setup) return '';
    return resolveSchoolDisplayName(setup.school_id, liveSchools, setup.school_name);
  }, [setup, liveSchools]);

  const cohortStudents = useMemo(() => {
    const mapped = mapCohortStudentsFromSetup(setup, students);
    if (mapped.length > 0) return mapped;
    return resolveStudents(setup?.student_ids || [], students);
  }, [setup, students]);

  const assignmentMap = useMemo(() => assignedStudentMap(squads), [squads]);

  const rosterStudents = useMemo(() => {
    const q = rosterSearch.trim().toLowerCase();
    return cohortStudents.filter((student) => {
      if (!q) return true;
      const name = formatStudentDisplayName(student.name).toLowerCase();
      return name.includes(q) || String(student.grade || '').toLowerCase().includes(q);
    });
  }, [cohortStudents, rosterSearch]);

  const rosterColumns = useMemo(
    () => chunkIntoColumns(rosterStudents, 5),
    [rosterStudents]
  );

  const unassignedIds = useMemo(() => {
    const assigned = new Set(assignmentMap.keys());
    return cohortStudents
      .map((s) => String(s.id))
      .filter((id) => !assigned.has(id));
  }, [cohortStudents, assignmentMap]);

  const onDragStart = useCallback((studentId, fromSlot) => {
    dragStudentId.current = String(studentId);
    dragFromSlot.current = fromSlot;
  }, []);

  const moveStudent = useCallback((studentId, toSlot) => {
    const sid = String(studentId);
    setSquads((prev) => {
      const next = prev.map((squad) => ({
        ...squad,
        member_ids: [...(squad.member_ids || []).map(String)],
      }));

      next.forEach((squad, index) => {
        squad.member_ids = squad.member_ids.filter((id) => id !== sid);
        if (index === toSlot) {
          squad.member_ids.push(sid);
        }
      });

      return next;
    });
  }, []);

  const removeToRoster = useCallback((slotIndex, studentId) => {
    const sid = String(studentId);
    setSquads((prev) => prev.map((squad, index) => (
      index === slotIndex
        ? { ...squad, member_ids: squad.member_ids.filter((id) => String(id) !== sid) }
        : squad
    )));
  }, []);

  const onDropOnSlot = useCallback((toSlot) => {
    const sid = dragStudentId.current;
    if (!sid || isLocked) return;
    moveStudent(sid, toSlot);
    dragStudentId.current = null;
    dragFromSlot.current = null;
    setDragOverSlot(null);
  }, [isLocked, moveStudent]);

  const onDropOnRoster = useCallback(() => {
    const sid = dragStudentId.current;
    const fromSlot = dragFromSlot.current;
    if (!sid || fromSlot === null || fromSlot === undefined || isLocked) return;
    removeToRoster(fromSlot, sid);
    dragStudentId.current = null;
    dragFromSlot.current = null;
  }, [isLocked, removeToRoster]);

  const handleAutoDistribute = () => {
    if (isLocked) return;
    setSquads((prev) => autoDistributeStudents(unassignedIds, prev));
  };

  const handleUnlock = async () => {
    const confirmed = window.confirm(
      'This squad setup has been finalized. Editing it may affect Year 2 operations. Do you want to unlock and edit?'
    );
    if (!confirmed) return;
    try {
      const data = await mentorApi.unlockYear2SquadSetup(cohortId);
      setSetup(data);
      setIsLocked(false);
      onSuccessRef.current?.('Squad setup unlocked for editing.');
    } catch (err) {
      onErrorRef.current?.(err?.message || 'Failed to unlock squad setup');
    }
  };

  const validateBeforeSave = () => {
    if (!setup || setup.is_blocked) {
      onErrorRef.current?.('This cohort cannot have squad setup.');
      return false;
    }
    if (squads.some((squad) => !squad.name_locked && (!squad.name.trim() || isSmokeTestSquadName(squad.name)))) {
      onErrorRef.current?.('All squad names are required.');
      return false;
    }
    if (squads.some((squad) => !squad.topic)) {
      onErrorRef.current?.('All squad topics are required.');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (isLocked) {
      onErrorRef.current?.('Unlock the finalized setup before saving changes.');
      return;
    }
    if (!validateBeforeSave()) return;

    const duplicates = findDuplicateTopics(squads);
    if (duplicates.length > 0) {
      const proceed = window.confirm(
        `Topics ${duplicates.join(', ')} are used more than once. Save anyway as draft?`
      );
      if (!proceed) return;
    }

    let allowUnassigned = false;
    if (unassignedIds.length > 0) {
      const proceed = window.confirm(
        'Some students are not assigned to any squad. Do you want to save this as draft?'
      );
      if (!proceed) return;
      allowUnassigned = true;
    }

    setSaving(true);
    try {
      const data = await mentorApi.saveYear2SquadSetup(cohortId, squads, { allowUnassigned });
      setSetup(data);
      setSquads(mergeSetupSquads(data.squads, data.required_squads, data.student_ids));
      setIsLocked(data.setup_status === 'finalized');
      onSaved?.(data);
      onSuccessRef.current?.('Squad setup saved as draft.');
    } catch (err) {
      onErrorRef.current?.(err?.message || 'Failed to save squad setup');
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async () => {
    if (isLocked) return;
    if (!validateBeforeSave()) return;
    if (unassignedIds.length > 0) {
      onErrorRef.current?.('All students must be assigned before finalizing.');
      return;
    }
    const duplicates = findDuplicateTopics(squads);
    if (duplicates.length > 0) {
      const proceed = window.confirm(
        `Topics ${duplicates.join(', ')} are duplicated. Finalize anyway?`
      );
      if (!proceed) return;
    }

    const confirmed = window.confirm(
      'Finalize will lock this squad setup for Year 2 operations. Continue?'
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      await mentorApi.saveYear2SquadSetup(cohortId, squads);
      const data = await mentorApi.finalizeYear2SquadSetup(cohortId);
      setSetup(data);
      setSquads(mergeSetupSquads(data.squads, data.required_squads, data.student_ids));
      setIsLocked(true);
      onSaved?.(data);
      onSuccessRef.current?.('Squad setup finalized.');
    } catch (err) {
      onErrorRef.current?.(err?.message || 'Failed to finalize squad setup');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="placeholder-card">
        <p>Loading Year 2 cohorts…</p>
      </div>
    );
  }

  if (!setup) {
    return (
      <div className="placeholder-card">
        <p>Unable to load squad setup.</p>
        <button type="button" className="btn-form-toggle-outlined" onClick={onBack}>← Back</button>
      </div>
    );
  }

  if (setup.is_blocked) {
    return (
      <div>
        <button type="button" className="btn-form-toggle-outlined" onClick={onBack} style={{ marginBottom: '16px' }}>
          ← Back to cohorts
        </button>
        <div className="placeholder-card">
          <h2>Squad setup blocked</h2>
          <p>{setup.blocked_message}</p>
        </div>
      </div>
    );
  }

  const squadGridClass = setup.required_squads === 4 ? 'year2-squad-grid-4' : 'year2-squad-grid-3';

  return (
    <div>
      <button type="button" className="btn-form-toggle-outlined" onClick={onBack} style={{ marginBottom: '16px' }}>
        ← Back to cohorts
      </button>

      <div className="year2-summary-bar">
        <div>
          <div className="year2-summary-label">School</div>
          <div className="year2-summary-value">{schoolDisplayName}</div>
        </div>
        <div>
          <div className="year2-summary-label">Cohort</div>
          <div className="year2-summary-value">{setup.cohort_name}</div>
        </div>
        <div>
          <div className="year2-summary-label">Students</div>
          <div className="year2-summary-value">{setup.student_count}</div>
        </div>
        <div>
          <div className="year2-summary-label">Required squads</div>
          <div className="year2-summary-value">{setup.required_squads}</div>
        </div>
        <div>
          <div className="year2-summary-label">Status</div>
          <div className="year2-summary-value">{formatSetupStatus(setup.setup_status)}</div>
        </div>
      </div>

      <div className="year2-roster-panel">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '16px' }}>Student roster</h3>
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            {unassignedIds.length} unassigned · drag into squads below
          </span>
        </div>
        <input
          type="text"
          className="wizard-search-input"
          placeholder="Search students…"
          value={rosterSearch}
          onChange={(e) => setRosterSearch(e.target.value)}
          style={{ marginBottom: '12px', width: '100%' }}
        />
        <div
          className="year2-roster-columns"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); onDropOnRoster(); }}
        >
          {rosterColumns.map((column, colIndex) => (
            <div key={`roster-col-${colIndex}`} className="year2-roster-column">
              {column.map((student) => {
                const assignedSlot = assignmentMap.get(String(student.id));
                const isAssigned = assignedSlot !== undefined;
                return (
                  <StudentChip
                    key={student.id}
                    student={student}
                    fromSlot={isAssigned ? assignedSlot : null}
                    isAssigned={isAssigned}
                    disabled={isLocked}
                    onDragStart={onDragStart}
                    onClick={(id) => {
                      if (isLocked) return;
                      if (isAssigned) removeToRoster(assignedSlot, id);
                    }}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn-form-toggle-outlined"
          disabled={isLocked || unassignedIds.length === 0}
          onClick={handleAutoDistribute}
        >
          Auto-distribute Students
        </button>
        {isLocked && (
          <button type="button" className="btn-form-toggle-outlined" onClick={handleUnlock}>
            Edit Finalized Setup
          </button>
        )}
      </div>

      <div className={squadGridClass}>
        {squads.map((squad, index) => (
          <SquadBox
            key={`squad-slot-${index}`}
            squad={squad}
            slotIndex={index}
            students={cohortStudents}
            isLocked={isLocked}
            isDragOver={dragOverSlot === index}
            onDragOverEnter={() => setDragOverSlot(index)}
            onDragOverLeave={() => setDragOverSlot((prev) => (prev === index ? null : prev))}
            onDrop={onDropOnSlot}
            onDragStart={onDragStart}
            onNameChange={(slot, value) => {
              setSquads((prev) => prev.map((item, idx) => (
                idx === slot ? { ...item, name: value } : item
              )));
            }}
            onTopicChange={(slot, value) => {
              setSquads((prev) => prev.map((item, idx) => (
                idx === slot ? { ...item, topic: value } : item
              )));
            }}
            onRemoveMember={removeToRoster}
          />
        ))}
      </div>

      <div className="year2-setup-actions">
        <p className="year2-setup-actions-help">
          <strong>Save Squad Setup</strong> keeps your work as a draft you can return to.
          <strong> Finalize Squad Setup</strong> locks the roster and topics for Year 2 operations.
        </p>
        <button
          type="button"
          className="btn-form-toggle-outlined year2-btn-save"
          disabled={saving || isLocked}
          onClick={handleSave}
        >
          {saving ? 'Saving…' : 'Save Squad Setup'}
        </button>
        {!isLocked && (
          <button
            type="button"
            className="btn-primary-action year2-btn-finalize"
            disabled={saving}
            onClick={handleFinalize}
          >
            {saving ? 'Finalizing…' : 'Finalize Squad Setup'}
          </button>
        )}
      </div>
    </div>
  );
}